import { Injectable } from '@angular/core';
import { Scope } from '../types/scope.type';
import { MsalService } from '@azure/msal-angular';
import { Observable, defer, firstValueFrom, switchMap, throwError, catchError, timeout } from 'rxjs';
import { AuthenticationResult, AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { getMsalTenantId } from 'app/core/auth/msal.factory';
import { SCOPES } from '../constants/scopes.constant';
import { persistStoredAccountSelection, resolveAccountForTenant } from 'app/core/auth/account-selection.util';

// Token acquisition timeout (30 seconds, in ms)
const TOKEN_TIMEOUT_MS = 30000;

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  private initializationPromise: Promise<void> | null = null;

  constructor(private readonly msalService: MsalService) {}

  acquireToken(scopes: Scope[], authority?: string, forceRefresh = false): Observable<AuthenticationResult> {
    return defer(() => this.ensureMsalInitialized()).pipe(
      switchMap(() => {
        const targetTenantId = authority ? this.extractTenantId(authority) : getMsalTenantId();
        const account = this.getAccountForTenant(targetTenantId);

        if (!account) {
          console.error('AuthorizationService: No account available for token acquisition', {
            targetTenantId,
            availableAccounts: this.msalService.instance.getAllAccounts().length,
          });
          return throwError(() => new Error('No active account available for token acquisition'));
        }

        const resolvedAuthority =
          authority ?? `https://login.microsoftonline.com/${account.tenantId ?? targetTenantId ?? 'organizations'}/`;

        this.ensureActiveAccount(account);

        return this.msalService
          .acquireTokenSilent({
            scopes,
            authority: resolvedAuthority,
            account,
            forceRefresh,
          })
          .pipe(
            timeout(TOKEN_TIMEOUT_MS),
            catchError((error) => {
              // Handle interaction required errors gracefully
              if (error instanceof InteractionRequiredAuthError) {
                console.warn('AuthorizationService: Interaction required for token acquisition');
              }
              return throwError(() => error);
            }),
          );
      }),
    );
  }

  /**
   * Check if the current user has a usable token for the current tenant.
   * This performs a real silent token acquisition to verify token validity.
   */
  async hasValidToken(scopes: Scope[] = [SCOPES.USER_IMPERSONATION]): Promise<boolean> {
    try {
      await this.ensureMsalInitialized();
      const targetTenantId = getMsalTenantId();
      const account = this.getAccountForTenant(targetTenantId);

      if (!account) {
        console.log('hasValidToken: No account found for tenant', targetTenantId);
        return false;
      }

      // Check if account has basic claims (fast check)
      if (!account.idTokenClaims) {
        console.log('hasValidToken: Account has no idTokenClaims');
        return false;
      }

      // Check token expiry from claims if available
      const exp = account.idTokenClaims['exp'] as number | undefined;
      let forceRefresh = false;
      if (exp) {
        const nowInSeconds = Math.floor(Date.now() / 1000);
        const bufferSeconds = 300; // 5 minute buffer
        if (exp < nowInSeconds + bufferSeconds) {
          console.log('hasValidToken: Token expired or expiring soon, will force refresh');
          forceRefresh = true;
        }
      }

      // Attempt silent token acquisition to verify we can actually get a valid token
      await firstValueFrom(
        this.msalService
          .acquireTokenSilent({
            scopes,
            authority: `https://login.microsoftonline.com/${account.tenantId ?? targetTenantId ?? 'organizations'}/`,
            account,
            forceRefresh,
          })
          .pipe(timeout(10000)), // Shorter timeout for validation
      );

      this.ensureActiveAccount(account);
      return true;
    } catch (error) {
      console.log('hasValidToken: Token validation failed', error instanceof Error ? error.message : error);
      return false;
    }
  }

  /**
   * Clear any stale authentication state and prepare for fresh login
   */
  clearAuthState(): void {
    try {
      // Clear active account - this is the safe way to reset auth state
      // without triggering a redirect
      this.msalService.instance.setActiveAccount(null);
    } catch (error) {
      console.error('Error clearing auth state:', error);
    }
  }

  private getAccountForTenant(targetTenantId?: string): AccountInfo | null {
    const allAccounts = this.msalService.instance.getAllAccounts();
    const activeAccount = this.msalService.instance.getActiveAccount();

    return resolveAccountForTenant(allAccounts, activeAccount, targetTenantId);
  }

  private ensureActiveAccount(account: AccountInfo): void {
    const currentActive = this.msalService.instance.getActiveAccount();
    if (
      !currentActive ||
      currentActive.tenantId !== account.tenantId ||
      currentActive.homeAccountId !== account.homeAccountId
    ) {
      this.msalService.instance.setActiveAccount(account);
    }

    persistStoredAccountSelection(account);
  }

  private extractTenantId(authority: string): string | undefined {
    const match = authority.match(/https?:\/\/[^/]+\/([^/]+)\/?/i);
    return match ? match[1] : undefined;
  }

  private ensureMsalInitialized(): Promise<void> {
    // If we have a cached promise (either pending or resolved), return it
    // This ensures all concurrent callers share the same initialization attempt
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Create new initialization promise
    // We store it immediately before awaiting to prevent concurrent calls from creating duplicates
    const initPromise = this.msalService.instance.initialize();
    this.initializationPromise = initPromise;

    // Handle failure: reset the cached promise only after the current attempt fully settles
    // This ensures all current waiters receive the same error, and only subsequent calls will retry
    initPromise.catch(() => {
      // Only reset if this is still the active promise (prevents race with a successful retry)
      if (this.initializationPromise === initPromise) {
        this.initializationPromise = null;
      }
    });

    return initPromise;
  }
}
